import { RefCallback, RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import {
  getTargetScrollTopForElementAtTopOfContainer,
  scrollElementToTopOfScrollContainer,
} from '../../../../../../../helpers/scroll-element-into-scroll-container';

type UseTranscriptionBlocksFollowScrollOptions = {
  /** When true, keeps the element wired via `activeBlockRefCallback` aligned to the top of the scroll container. */
  followPlayback: boolean;
  /** Drives when follow-scroll runs (e.g. current transcript block id). */
  activeBlockId: string | null;
  /** Called when the user scrolls the list while not in a programmatic follow scroll. */
  onUserInterruptedScroll: () => void;
};

type UseTranscriptionBlocksFollowScrollResult = {
  scrollContainerRef: RefObject<HTMLDivElement>;
  /**
   * Pass as `ref` on the row that is the current follow-scroll target (same as `activeBlockId`),
   * or `undefined` on other rows — avoids `RefObject` / `forwardRef` generic mismatches.
   */
  activeBlockRefCallback: RefCallback<HTMLDivElement>;
  /** Attach to the scroll container as `onScroll`. */
  handleContainerScroll: () => void;
};

/**
 * Follow-playback scrolling for a transcript block list: scrolls the active block into view when
 * `followPlayback` is on, uses a ref flag cleared on `scrollend` so programmatic scroll does not
 * count as a user interruption.
 */
export function useTranscriptionBlocksFollowScroll({
  followPlayback,
  activeBlockId,
  onUserInterruptedScroll,
}: UseTranscriptionBlocksFollowScrollOptions): UseTranscriptionBlocksFollowScrollResult {
  const activeBlockRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isProgrammaticFollowScrollRef = useRef(false);

  const activeBlockRefCallback = useCallback((el: HTMLDivElement | null) => {
    activeBlockRef.current = el;
  }, []);

  useLayoutEffect(() => {
    if (!followPlayback || !activeBlockId) {
      return;
    }
    const el = activeBlockRef.current;
    const container = scrollContainerRef.current;

    if (!el) {
      return;
    }

    if (container) {
      const targetTop = getTargetScrollTopForElementAtTopOfContainer(container, el);
      const alreadyAligned = Math.abs(container.scrollTop - targetTop) < 0.5;
      if (alreadyAligned) {
        return;
      }
      isProgrammaticFollowScrollRef.current = true;
      scrollElementToTopOfScrollContainer(container, el);
    } else {
      isProgrammaticFollowScrollRef.current = true;
      el.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' });
      const clearFlag = () => {
        isProgrammaticFollowScrollRef.current = false;
      };
      window.addEventListener('scrollend', clearFlag, { once: true });
    }
  }, [followPlayback, activeBlockId]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    const clearProgrammaticFollowScroll = () => {
      isProgrammaticFollowScrollRef.current = false;
    };
    container.addEventListener('scrollend', clearProgrammaticFollowScroll);
    return () => {
      container.removeEventListener('scrollend', clearProgrammaticFollowScroll);
    };
  }, []);

  const handleContainerScroll = () => {
    if (isProgrammaticFollowScrollRef.current) {
      return;
    }
    onUserInterruptedScroll();
  };

  return {
    scrollContainerRef,
    activeBlockRefCallback,
    handleContainerScroll,
  };
}
